import { useEffect, useMemo, useRef } from "react";
import { useGetAdmissionsForMe } from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import {
  ADMISSIONS_MAX_POLL_DURATION_MS,
  getAdmissionsPollIntervalMs,
  mergeAdmissionsIntoFormSchoolRanks,
  ParentFormSchoolRank,
  shouldPollAdmissions,
} from "../admissions";

export function useFormAdmissions(
  formId: string,
  gqlRanks: readonly ParentFormSchoolRank[]
) {
  const mountedAt = useRef(Date.now());
  const clientOptions = useMemo(
    () => ({ path: { formId } as FSR.GetAdmissionsForMeData["path"] }),
    [formId]
  );

  const admissionsQuery = useGetAdmissionsForMe(clientOptions, undefined, {
    enabled: Boolean(formId),
    placeholderData: (previousData) => previousData,
    refetchOnMount: "always",
    refetchInterval: (query) => {
      if (Date.now() - mountedAt.current >= ADMISSIONS_MAX_POLL_DURATION_MS) {
        return false;
      }

      const admissions = query.state.data?.data;
      if (!admissions || !shouldPollAdmissions(admissions)) {
        return false;
      }

      const pollAttempt = Math.max(0, query.state.dataUpdateCount - 1);
      return getAdmissionsPollIntervalMs(pollAttempt);
    },
  });

  useEffect(() => {
    const stopPollingTimer = window.setTimeout(() => {
      void admissionsQuery.refetch();
    }, ADMISSIONS_MAX_POLL_DURATION_MS);

    return () => {
      window.clearTimeout(stopPollingTimer);
    };
  }, [admissionsQuery]);

  const formSchoolRanks = useMemo(
    () =>
      mergeAdmissionsIntoFormSchoolRanks(gqlRanks, admissionsQuery.data?.data),
    [admissionsQuery.data, gqlRanks]
  );

  const isAdmissionsUpdating =
    admissionsQuery.isFetching && !admissionsQuery.isLoading;

  return {
    formSchoolRanks,
    isAdmissionsUpdating,
    isAdmissionsLoading: admissionsQuery.isLoading,
  };
}
